import MenuButton, {
  BaseMenuButtonProps
} from 'navigation/components/Header/components/MenuButton'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { setCurrentEstablishment } from 'reduxStore/slices'
import { useAppDispatch, useAppSelector } from 'reduxStore/store'
import { getUserEstablishment } from 'services/establishment'
import Storage from 'storage/auth'
import { IEstablishmentResponse, IUserSession } from 'types'
import DomainIcon from '@mui/icons-material/Domain'
import {
  Grid2 as Grid,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@mui/material'
import { patchSession } from 'services/user'
import { EstablishmentManagerVisible } from 'components/VisibleChild'
import { SettingsOutlined } from '@mui/icons-material'

const useEstablishmentMenu = () => {
  const [state, setState] = useState<IStateStdTypes>('idle')
  const location = useLocation()

  const [options, setOptions] = useState<Array<IEstablishmentResponse>>([])

  const user = Storage.user.get() as IUserSession

  const establishmentData = useAppSelector(rState => rState.establishment.data)

  const establishmentId = establishmentData?.establishment_id

  const navigate = useNavigate()

  const dispatch = useAppDispatch()

  const handleOnSelect: BaseMenuButtonProps['onSelect'] = value => {
    if (value) {
      const establishment = options.find(item => item.establishment_id == value)

      if (establishment?.establishment_id !== establishmentId) {
        patchSession({
          current_establishment_id: establishment?.establishment_id ?? '',
          user_id: user.professional_id
        })
      }
      dispatch(setCurrentEstablishment(establishment))
      navigate(`/`, { replace: true })
    }
  }

  /** Effect to fetch establishments
   * This effect fetches the user's establishments and sets the options for the menu
   */
  useEffect(() => {
    const fetchEstablishments = async () => {
      setState('loading')
      const response = await getUserEstablishment(user.professional_id)
      if (response.success) {
        const result = response.data ?? []
        if (result.length > 0) {
          if (!establishmentId) {
            dispatch(setCurrentEstablishment(result[0]))
          } else if (!establishmentData.commercial_name) {
            const establishment = result.find(
              item => item.establishment_id == establishmentId
            )
            dispatch(setCurrentEstablishment(establishment))
          }
        }
        setOptions(result)
        setState('success')
      } else {
        setState('error')
      }
    }
    if (state != 'loading') {
      fetchEstablishments()
    }
  }, [location])

  return {
    options,
    establishmentId,
    handleOnSelect
  }
}

/**
 * EstablishmentMenu component
 * This component renders a menu for selecting an establishment.
 * @returns JSX.Element
 */
const EstablishmentMenu: React.FC = () => {
  const { options, establishmentId, handleOnSelect } = useEstablishmentMenu()

  const value =
    options?.find(
      ({ establishment_id }) => establishment_id === establishmentId
    )?.commercial_name ?? ''

  if (options.length == 0 || !value) {
    return <></>
  }

  return (
    <EstablishmentManagerVisible>
      <Grid
        container
        alignItems={'center'}
        spacing={2}
        color={'primarySoft.main'}
      >
        <Tooltip title={value}>
          <Grid container spacing={1} alignItems={'center'}>
            <DomainIcon />
            <Typography
              sx={{
                color: 'defaultComponent.contrastText',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '250px'
              }}
            >
              {value}
            </Typography>
          </Grid>
        </Tooltip>
        <MenuButton
          icon={<SettingsOutlined sx={{ color: 'primarySoft.main' }} />}
          options={options.map(item => ({
            label: item.commercial_name,
            value: item.establishment_id
          }))}
          value={establishmentId}
          startIcon={establishmentId ? <DomainIcon /> : undefined}
          onSelect={handleOnSelect}
        />
      </Grid>
    </EstablishmentManagerVisible>
  )
}

/**
 * EstablishmentMenuMobile component
 * This component renders a menu for selecting an establishment with a selectBox field.
 * @returns JSX.Element
 */
export const EstablishmentMenuMobile: React.FC<{
  short?: boolean
}> = ({ short }) => {
  const { options, establishmentId, handleOnSelect } = useEstablishmentMenu()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open: boolean = !!anchorEl

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const value =
    options?.find(
      ({ establishment_id }) => establishment_id === establishmentId
    )?.commercial_name ?? ''

  if (options.length == 0 || !value) {
    return <></>
  }

  return (
    <>
      <Grid
        onClick={e => {
          e.stopPropagation()
          handleClick(e)
        }}
        container
        alignItems={'center'}
        spacing={2}
        color={'primarySoft.main'}
        flex={1}
        flexWrap={'nowrap'}
      >
        <span
          style={{ color: 'var(--primarySoft-button-color)' }}
          className='material-symbols-outlined'
        >
          settings
        </span>
        {!short && (
          <>
            <Tooltip title={value}>
              <Typography>{value}</Typography>
            </Tooltip>

            <span
              style={{ color: 'var(--primarySoft-button-color)' }}
              className='material-symbols-outlined'
            >
              change_circle
            </span>
          </>
        )}
      </Grid>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {options.map(({ establishment_id, commercial_name }, index) => {
          const isSelected = value === commercial_name
          return (
            <Tooltip key={index} title={commercial_name} placement='right'>
              <MenuItem
                key={`menu-item-${index}`}
                onClick={e => {
                  handleOnSelect(isSelected ? undefined : establishment_id)
                  handleClose()
                }}
                sx={{
                  backgroundColor: isSelected ? 'action.selected' : undefined,
                  width: 'auto'
                }}
              >
                {commercial_name}
              </MenuItem>
            </Tooltip>
          )
        })}
      </Menu>
    </>
  )
}

export default EstablishmentMenu
